import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { useEmailVerificationGate } from '../../hooks/useEmailVerificationGate';
import { Button } from '../../components/ui/Button';
import { ReviewList, WineReviewForm, type WineReviewFormData } from '../../components/reviews';
import { useReviewPhotoUpload } from '../../hooks/useReviewPhotoUpload';

interface WineReviewsProps {
  wineId: string;
  wineName: string;
}

export const WineReviews = ({ wineId, wineName }: WineReviewsProps) => {
  const { user } = useAuth();
  const { requireVerified, gateModal } = useEmailVerificationGate();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const utils = trpc.useUtils();

  const {
    uploadPhotos,
    uploadStates: photoUploadStates,
    isUploading: isUploadingPhotos,
    reset: resetPhotoUploads,
  } = useReviewPhotoUpload(wineId);

  const createReviewMutation = trpc.wineReview.create.useMutation({
    onSuccess: () => {
      resetPhotoUploads();
      setShowReviewForm(false);
      utils.wineReview.list.invalidate({ wineId });
    },
  });

  const handleSubmitReview = async (data: WineReviewFormData) => {
    const imageIds = await uploadPhotos(data.photos);

    await createReviewMutation.mutateAsync({
      wineId,
      title: data.title,
      body: data.body,
      ratings: {
        score: data.ratings.score,
        valueForMoney: data.ratings.valueForMoney,
      },
      imageIds,
    });
  };

  const handleWriteReviewClick = () => {
    if (!requireVerified()) return;
    setShowReviewForm(true);
  };

  return (
    <div className="border-t border-gray-200 pt-6">
      {gateModal}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Reviews</h2>
        {user && !showReviewForm && (
          <Button variant="outline" onClick={handleWriteReviewClick}>
            Write a Review
          </Button>
        )}
      </div>

      {showReviewForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Write a Review for {wineName}
          </h3>
          {createReviewMutation.error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {createReviewMutation.error.message}
            </div>
          )}
          <WineReviewForm
            onSubmit={handleSubmitReview}
            onCancel={() => setShowReviewForm(false)}
            isSubmitting={createReviewMutation.isPending || isUploadingPhotos}
            photoUploadStates={photoUploadStates}
          />
        </div>
      )}

      <ReviewList entityId={wineId} reviewType="wine" />
    </div>
  );
};
